import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { LlmForm, type LlmFormValues } from "@/components/llm-form";

export const Route = createFileRoute("/_authed/llms/$id")({
  component: EditLlmPage,
});

function EditLlmPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin", "llm", id],
    queryFn: async () => {
      const { data, error: apiError } = await api.GET(
        "/api/v1/admin/llms/{llm_id}",
        { params: { path: { llm_id: id } } },
      );
      if (apiError) throw new Error("not found");
      return data!;
    },
  });

  const update = useMutation({
    mutationFn: async (values: LlmFormValues) => {
      const body: Record<string, unknown> = {
        name: values.name,
        provider: values.provider,
        provider_model: values.provider_model,
        notes: values.notes || null,
        is_active: values.is_active,
      };
      // Only send api_key if user typed something (don't rotate by default).
      if (values.api_key !== "") body.api_key = values.api_key;
      const { error: apiError } = await api.PATCH(
        "/api/v1/admin/llms/{llm_id}",
        {
          params: { path: { llm_id: id } },
          body: body as never,
        },
      );
      if (apiError) {
        const detail = (apiError as { detail?: string })?.detail ?? "update failed";
        throw new Error(String(detail));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "llms"] });
      qc.invalidateQueries({ queryKey: ["admin", "llm", id] });
      navigate({ to: "/llms" });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "update failed"),
  });

  return (
    <div className="space-y-4">
      <Link
        to="/llms"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
      >
        <ChevronLeft className="size-4" />
        All LLMs
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>Edit LLM</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isPending ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : q.isError || !q.data ? (
            <p className="text-sm text-red-600">Failed to load LLM.</p>
          ) : (
            <LlmForm
              mode="edit"
              hasApiKey={q.data.has_api_key}
              initial={{
                code: q.data.code,
                name: q.data.name,
                provider: q.data.provider,
                provider_model: q.data.provider_model,
                is_active: q.data.is_active,
                notes: q.data.notes ?? "",
                api_key: "",
              }}
              submitting={update.isPending}
              error={error}
              onSubmit={(v) => {
                setError(null);
                update.mutate(v);
              }}
              onCancel={() => navigate({ to: "/llms" })}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
